/**
 * O convite para instalar o app no celular.
 *
 * Existe porque o convite nativo do navegador **não volta depois de
 * desinstalar**: o Chrome decide sozinho quando mostrar a barrinha dele, e
 * quem removeu o app fica sem caminho visível para reinstalar. O evento
 * continua sendo disparado — o que faltava era alguém oferecê-lo.
 *
 * Só no celular, e de propósito: no desktop instalar um app de atendimento
 * não resolve nada (não há aviso de tela bloqueada nem ícone de gaveta que
 * importe), e o convite viraria ruído em quem usa o dia inteiro.
 *
 * A dispensa é lembrada por 30 dias — não para sempre. "Agora não" é agora,
 * não é "nunca mais": alguém que trocou de celular, ou que desinstalou sem
 * querer, precisa reencontrar o caminho sem ter de limpar dados do site.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Download, Share, X } from "lucide-react";

import { useInstalavel } from "./useInstalavel";

const CHAVE = "t2c.instalar.dispensado";
const DIAS_DE_SILENCIO = 30;

function dispensadoRecentemente(): boolean {
  try {
    const quando = Number(localStorage.getItem(CHAVE));
    if (!Number.isFinite(quando) || quando <= 0) return false;
    return Date.now() - quando < DIAS_DE_SILENCIO * 24 * 3_600_000;
  } catch {
    // Aba anônima, cookies bloqueados: sem memória, oferece.
    return false;
  }
}

function lembrarDispensa(): void {
  try {
    localStorage.setItem(CHAVE, String(Date.now()));
  } catch {
    // Não poder lembrar não pode impedir de dispensar.
  }
}

/** Celular ou tablet? O convite não vale no desktop. */
function ePortatil(): boolean {
  try {
    return window.matchMedia("(pointer: coarse)").matches;
  } catch {
    return false;
  }
}

export function InstallPrompt() {
  const { t } = useTranslation();
  const instalacao = useInstalavel();
  const [dispensado, setDispensado] = useState(() => dispensadoRecentemente());
  const [verInstrucoes, setVerInstrucoes] = useState(false);

  if (dispensado || !ePortatil()) return null;
  if (instalacao.modo === "indisponivel") return null;

  const dispensar = () => {
    lembrarDispensa();
    setDispensado(true);
  };

  return (
    <div
      role="status"
      className="fixed inset-x-0 bottom-0 z-50 border-t border-gray-200 bg-white px-4 py-3 shadow-lg dark:border-gray-700 dark:bg-gray-800"
      style={{ paddingBottom: "max(0.75rem, var(--safe-bottom))" }}
    >
      <div className="flex items-center gap-3">
        <Download className="h-5 w-5 shrink-0 text-primary-500" />
        <p className="flex-1 text-sm text-gray-700 dark:text-gray-200">
          {t("pwa.installInvite")}
        </p>

        {instalacao.modo === "prompt" ? (
          <button
            type="button"
            onClick={() => void instalacao.instalar()}
            className="shrink-0 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700"
          >
            {t("pwa.install")}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setVerInstrucoes((v) => !v)}
            className="shrink-0 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700"
          >
            {t("pwa.installHow")}
          </button>
        )}

        <button
          type="button"
          onClick={dispensar}
          aria-label={t("common.close")}
          className="shrink-0 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* iPhone: não há API que instale, então o caminho é dito em palavras.
          Sem isto a conclusão de quem usa iPhone é "o app não instala". */}
      {instalacao.modo === "ios" && verInstrucoes && (
        <p className="mt-2 flex items-start gap-1.5 text-xs text-gray-600 dark:text-gray-300">
          <Share className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          {t("pwa.installIosSteps")}
        </p>
      )}
    </div>
  );
}

export default InstallPrompt;
